/**
 * Upload Page
 *
 * Main document upload interface with batch upload,
 * extraction preview, and manual correction workflow.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';
import { createBatchUpload } from '../../components/upload/BatchUpload.js';
import { createExtractionPreview } from '../../components/upload/ExtractionPreview.js';
import { apiClient } from '../../services/api.js';

/**
 * Page state
 */
const state = {
  view: 'upload', // 'upload' | 'preview' | 'success'
  uploadedDocuments: [],
  selectedDocumentId: null,
  currentPreviewIndex: 0,
  recentUploads: [],
  recentUploadsLoading: true,
};

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  const lang = getCurrentLanguage();

  if (diffMins < 1) {
    return lang === 'fr' ? 'À l\'instant' : lang === 'ar' ? 'الآن' : 'Just now';
  }
  if (diffMins < 60) {
    return lang === 'fr' ? `Il y a ${diffMins} min` : lang === 'ar' ? `منذ ${diffMins} دقيقة` : `${diffMins} min ago`;
  }
  if (diffHours < 24) {
    return lang === 'fr' ? `Il y a ${diffHours}h` : lang === 'ar' ? `منذ ${diffHours} ساعة` : `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return lang === 'fr' ? `Il y a ${diffDays}j` : lang === 'ar' ? `منذ ${diffDays} يوم` : `${diffDays}d ago`;
  }
  return formatDate(dateStr);
}

/**
 * Extract items from API response (handles nested response structure)
 */
function extractItemsFromResponse(response) {
  // API returns: { success, data: { data: [...], pageInfo } } or { success, data: [...] }
  let data = response?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    // Check for nested data (PaginatedResponse)
    if (data.data !== undefined) {
      data = data.data;
    }
  }
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch recent uploads (invoices and contracts)
 */
async function fetchRecentUploads() {
  state.recentUploadsLoading = true;

  try {
    // Fetch both invoices and contracts in parallel
    const [invoicesResponse, contractsResponse] = await Promise.all([
      apiClient.get('/v1/invoices?pageSize=10').catch(() => ({ data: { data: [] } })),
      apiClient.get('/v1/contracts?pageSize=10').catch(() => ({ data: { data: [] } })),
    ]);

    // Extract items from responses using helper
    const invoiceItems = extractItemsFromResponse(invoicesResponse);
    const contractItems = extractItemsFromResponse(contractsResponse);

    const invoices = invoiceItems.map(inv => ({
      id: inv.id,
      type: 'invoice',
      name: inv.invoiceNumber || inv.filename || `Invoice ${inv.id?.slice(-6)}`,
      vendor: inv.vendorName || inv.vendor?.name || '-',
      date: inv.createdAt || inv.uploadedAt,
      status: inv.status || 'pending',
      amount: inv.total || inv.totalAmount,
      currency: inv.currency || 'EUR',
    }));

    const contracts = contractItems.map(con => ({
      id: con.id,
      type: 'contract',
      name: con.title || con.contractNumber || con.filename || `Contract ${con.id?.slice(-6)}`,
      vendor: con.vendorName || con.vendor?.name || '-',
      date: con.createdAt || con.uploadedAt,
      status: con.status || 'active',
      value: con.totalValue,
      currency: con.currency || 'EUR',
    }));

    // Combine and sort by date (most recent first)
    const combined = [...invoices, ...contracts];
    combined.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Take only the 10 most recent
    state.recentUploads = combined.slice(0, 10);

  } catch (error) {
    console.error('Error fetching recent uploads:', error);
    state.recentUploads = [];
  } finally {
    state.recentUploadsLoading = false;
    renderRecentUploads();
  }
}

/**
 * Render recent uploads section
 */
function renderRecentUploads() {
  const container = document.getElementById('recent-uploads-list');
  if (!container) return;

  if (state.recentUploadsLoading) {
    container.innerHTML = `
      <div class="section-loading">
        <div class="spinner"></div>
        <span>${t('common.loading')}</span>
      </div>
    `;
    return;
  }

  // Show real data only - no mock data fallback
  const uploads = state.recentUploads;

  if (uploads.length === 0) {
    container.innerHTML = `<p class="empty-message">${t('upload.noRecentUploads')}</p>`;
    return;
  }

  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';

  container.innerHTML = uploads.map(upload => {
    const isInvoice = upload.type === 'invoice';
    const icon = isInvoice ? `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
    ` : `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <path d="M12 18v-6"/>
        <path d="M9 15l3-3 3 3"/>
      </svg>
    `;

    const statusClass = getStatusClass(upload.status);
    const statusLabel = getStatusLabel(upload.status);

    const amount = isInvoice && upload.amount
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: upload.currency || getCurrency(), minimumFractionDigits: 0 }).format(upload.amount)
      : upload.value
        ? new Intl.NumberFormat(locale, { style: 'currency', currency: upload.currency || getCurrency(), minimumFractionDigits: 0 }).format(upload.value)
        : '';

    return `
      <div class="recent-upload-item" data-id="${upload.id}" data-type="${upload.type}">
        <div class="recent-upload-icon ${upload.type}">
          ${icon}
        </div>
        <div class="recent-upload-info">
          <div class="recent-upload-name">${upload.name}</div>
          <div class="recent-upload-vendor">${upload.vendor}</div>
        </div>
        <div class="recent-upload-meta">
          <span class="recent-upload-amount">${amount}</span>
          <span class="recent-upload-time">${formatRelativeTime(upload.date)}</span>
        </div>
        <div class="recent-upload-status">
          <span class="status-badge ${statusClass}">${statusLabel}</span>
        </div>
        <button class="recent-upload-action" title="${t('common.viewDetails')}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>
    `;
  }).join('');

  // Add click handlers
  container.querySelectorAll('.recent-upload-item').forEach(item => {
    item.addEventListener('click', () => {
      const id = item.dataset.id;
      const type = item.dataset.type;
      window.location.hash = type === 'invoice' ? `#invoices/${id}` : `#contracts/${id}`;
    });
  });
}

/**
 * Get status CSS class
 */
function getStatusClass(status) {
  const statusMap = {
    pending: 'status--warning',
    needs_review: 'status--warning',
    extracted: 'status--success',
    approved: 'status--success',
    active: 'status--success',
    expired: 'status--error',
    rejected: 'status--error',
    draft: 'status--neutral',
  };
  return statusMap[status] || 'status--neutral';
}

/**
 * Get translated status label
 */
function getStatusLabel(status) {
  const labelMap = {
    pending: t('common.pending'),
    needs_review: t('invoices.needsReview'),
    extracted: t('status.extracted') || 'Extracted',
    approved: t('invoices.approved'),
    active: t('status.active'),
    expired: t('status.expired'),
    rejected: t('status.rejected') || 'Rejected',
    draft: t('status.draft'),
  };
  return labelMap[status] || status;
}

/**
 * Handle successful upload
 */
function handleUploadSuccess(documents) {
  state.uploadedDocuments = documents;
  if (documents.length > 0) {
    state.currentPreviewIndex = 0;
    state.selectedDocumentId = documents[0].id;
    state.view = 'preview';
  } else {
    state.view = 'success';
  }
  // Refresh recent uploads after successful upload
  fetchRecentUploads();
  render();
}

/**
 * Navigate to next document preview
 */
function nextDocument() {
  if (state.currentPreviewIndex < state.uploadedDocuments.length - 1) {
    state.currentPreviewIndex++;
    state.selectedDocumentId = state.uploadedDocuments[state.currentPreviewIndex].id;
    render();
  }
}

/**
 * Navigate to previous document preview
 */
function prevDocument() {
  if (state.currentPreviewIndex > 0) {
    state.currentPreviewIndex--;
    state.selectedDocumentId = state.uploadedDocuments[state.currentPreviewIndex].id;
    render();
  }
}

/**
 * Select document from list
 */
function selectDocument(docId) {
  const index = state.uploadedDocuments.findIndex(d => d.id === docId);
  if (index !== -1) {
    state.currentPreviewIndex = index;
    state.selectedDocumentId = docId;
    render();
  }
}

/**
 * Finish review and go back to upload
 */
function finishReview() {
  state.view = 'upload';
  state.uploadedDocuments = [];
  state.selectedDocumentId = null;
  state.currentPreviewIndex = 0;
  render();
}

/**
 * Render upload view
 */
function renderUploadView() {
  return `
    <div class="upload-page">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">${t('upload.title')}</h1>
        <p class="page-subtitle">${t('upload.subtitle')}</p>
      </div>

      <!-- Batch Upload Component -->
      <div id="batch-upload-container"></div>

      <!-- Recent Uploads -->
      <div class="recent-uploads">
        <h2 class="section-title">${t('upload.recentUploads')}</h2>
        <div id="recent-uploads-list" class="recent-uploads-list">
          <p class="empty-message">${t('upload.noRecentUploads')}</p>
        </div>
      </div>

      <!-- Upload Tips -->
      <div class="upload-tips">
        <h3 class="tips-title">${t('upload.tips.title')}</h3>
        <ul class="tips-list">
          <li>${t('upload.tips.formats')}</li>
          <li>${t('upload.tips.quality')}</li>
          <li>${t('upload.tips.batch')}</li>
          <li>${t('upload.tips.review')}</li>
        </ul>
      </div>
    </div>
  `;
}

/**
 * Render preview view
 */
function renderPreviewView() {
  const currentDoc = state.uploadedDocuments[state.currentPreviewIndex];
  const total = state.uploadedDocuments.length;
  const current = state.currentPreviewIndex + 1;

  return `
    <div class="preview-page">
      <!-- Preview Header -->
      <div class="preview-header">
        <button class="btn btn-secondary btn-back" id="back-to-upload">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M19 12H5M12 19l-7-7 7-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          ${t('common.back')}
        </button>
        
        <div class="preview-navigation">
          ${total > 1 ? `
            <button class="nav-btn" id="prev-doc" ${state.currentPreviewIndex === 0 ? 'disabled' : ''}>
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <span class="nav-counter">${current} / ${total}</span>
            <button class="nav-btn" id="next-doc" ${state.currentPreviewIndex === total - 1 ? 'disabled' : ''}>
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          ` : ''}
        </div>

        <button class="btn btn-primary" id="finish-review">
          ${t('upload.finishReview')}
        </button>
      </div>

      <!-- Document Thumbnails (for multiple uploads) -->
      ${total > 1 ? `
        <div class="document-thumbnails">
          ${state.uploadedDocuments.map((doc, index) => `
            <div class="thumbnail ${index === state.currentPreviewIndex ? 'active' : ''}" 
                 data-doc-id="${doc.id}"
                 data-index="${index}">
              <div class="thumbnail-icon">
                ${getDocumentIcon(doc.contentType)}
              </div>
              <span class="thumbnail-name">${truncateFilename(doc.filename, 15)}</span>
              <span class="thumbnail-status ${doc.status}">${doc.status}</span>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <!-- Extraction Preview Component -->
      <div id="extraction-preview-container"></div>
    </div>
  `;
}

/**
 * Render success view
 */
function renderSuccessView() {
  return `
    <div class="success-page">
      <div class="success-content">
        <div class="success-icon">
          <svg viewBox="0 0 24 24" width="64" height="64">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
            <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2>${t('upload.success.title')}</h2>
        <p>${t('upload.success.message')}</p>
        <div class="success-actions">
          <button class="btn btn-secondary" id="upload-more">
            ${t('upload.success.uploadMore')}
          </button>
          <button class="btn btn-primary" id="view-documents">
            ${t('upload.success.viewDocuments')}
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Get document icon based on content type
 */
function getDocumentIcon(contentType) {
  if (contentType?.includes('pdf')) {
    return `<svg viewBox="0 0 24 24" width="24" height="24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="2"/>
      <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="2"/>
      <text x="12" y="16" text-anchor="middle" font-size="6" fill="currentColor">PDF</text>
    </svg>`;
  }
  if (contentType?.includes('image')) {
    return `<svg viewBox="0 0 24 24" width="24" height="24">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill="none" stroke="currentColor" stroke-width="2"/>
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
      <polyline points="21 15 16 10 5 21" fill="none" stroke="currentColor" stroke-width="2"/>
    </svg>`;
  }
  if (contentType?.includes('spreadsheet') || contentType?.includes('csv') || contentType?.includes('excel')) {
    return `<svg viewBox="0 0 24 24" width="24" height="24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="2"/>
      <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="2"/>
      <line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" stroke-width="2"/>
      <line x1="8" y1="17" x2="16" y2="17" stroke="currentColor" stroke-width="2"/>
    </svg>`;
  }
  return `<svg viewBox="0 0 24 24" width="24" height="24">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" stroke-width="2"/>
    <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" stroke-width="2"/>
  </svg>`;
}

/**
 * Truncate filename for display
 */
function truncateFilename(filename, maxLength) {
  if (!filename || filename.length <= maxLength) return filename;
  const ext = filename.lastIndexOf('.');
  if (ext > 0) {
    const name = filename.substring(0, ext);
    const extension = filename.substring(ext);
    const truncated = name.substring(0, maxLength - extension.length - 3) + '...';
    return truncated + extension;
  }
  return filename.substring(0, maxLength - 3) + '...';
}

/**
 * Main render function
 */
function render() {
  const container = document.getElementById('upload-page');
  if (!container) {
    console.error('Upload page container not found');
    return;
  }

  console.log('[UploadPage] render() called, state:', {
    view: state.view,
    documentCount: state.uploadedDocuments?.length,
    selectedDocumentId: state.selectedDocumentId,
    currentPreviewIndex: state.currentPreviewIndex,
  });

  switch (state.view) {
    case 'upload':
      container.innerHTML = renderUploadView();
      // Initialize batch upload component
      const batchContainer = document.getElementById('batch-upload-container');
      if (batchContainer) {
        const batchUpload = createBatchUpload({
          onSuccess: handleUploadSuccess,
          onError: (error) => console.error('Upload error:', error),
        });
        batchContainer.appendChild(batchUpload);
      }
      // Fetch and render recent uploads
      fetchRecentUploads();
      // Inject recent uploads styles
      injectRecentUploadsStyles();
      break;
      
    case 'preview':
      console.log('[UploadPage] Rendering preview view for document:', state.selectedDocumentId);
      container.innerHTML = renderPreviewView();
      // Initialize extraction preview component
      const previewContainer = document.getElementById('extraction-preview-container');
      console.log('[UploadPage] Preview container found:', !!previewContainer);
      if (previewContainer && state.selectedDocumentId) {
        console.log('[UploadPage] Creating ExtractionPreview for:', state.selectedDocumentId);
        const preview = createExtractionPreview(state.selectedDocumentId);
        console.log('[UploadPage] Preview element created:', preview?.id, preview?.className);
        previewContainer.appendChild(preview);
        console.log('[UploadPage] Preview appended to container');
      } else {
        console.error('[UploadPage] Cannot create preview - container:', !!previewContainer, 'documentId:', state.selectedDocumentId);
      }
      break;
      
    case 'success':
      container.innerHTML = renderSuccessView();
      break;
  }

  attachEventListeners();
}

/**
 * Attach event listeners
 */
function attachEventListeners() {
  // Back to upload button
  document.getElementById('back-to-upload')?.addEventListener('click', finishReview);

  // Navigation buttons
  document.getElementById('prev-doc')?.addEventListener('click', prevDocument);
  document.getElementById('next-doc')?.addEventListener('click', nextDocument);

  // Finish review button
  document.getElementById('finish-review')?.addEventListener('click', () => {
    state.view = 'success';
    render();
  });

  // Thumbnail clicks
  document.querySelectorAll('.thumbnail').forEach(thumb => {
    thumb.addEventListener('click', () => {
      selectDocument(thumb.dataset.docId);
    });
  });

  // Success page actions
  document.getElementById('upload-more')?.addEventListener('click', () => {
    finishReview();
  });
  document.getElementById('view-documents')?.addEventListener('click', () => {
    // Navigate based on the uploaded document type
    const lastDoc = state.uploadedDocuments?.[0];
    const docType = lastDoc?.documentType || lastDoc?.type || 'contract';
    if (docType === 'invoice' || docType === 'INVOICE') {
      window.location.hash = '#invoices';
    } else {
      window.location.hash = '#contracts';
    }
  });
}

/**
 * Inject styles for recent uploads
 */
function injectRecentUploadsStyles() {
  if (document.getElementById('recent-uploads-styles')) return;

  const styles = document.createElement('style');
  styles.id = 'recent-uploads-styles';
  styles.textContent = `
    .recent-uploads {
      margin-top: var(--space-8);
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      padding: var(--space-6);
    }

    .recent-uploads .section-title {
      font-size: var(--text-lg);
      font-weight: 600;
      margin: 0 0 var(--space-4);
      color: var(--text-primary);
    }

    .recent-uploads-list {
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
    }

    .recent-uploads-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-3);
      padding: var(--space-6);
      color: var(--text-secondary);
    }

    .recent-uploads-loading .spinner {
      width: 24px;
      height: 24px;
      border: 2px solid var(--border);
      border-top-color: var(--primary);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    .recent-upload-item {
      display: flex;
      align-items: center;
      gap: var(--space-4);
      padding: var(--space-3) var(--space-4);
      background: var(--background);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .recent-upload-item:hover {
      border-color: var(--primary);
      background: var(--primary-light);
    }

    .recent-upload-icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius);
      flex-shrink: 0;
    }

    .recent-upload-icon.invoice {
      background: var(--info-light);
      color: var(--info);
    }

    .recent-upload-icon.contract {
      background: var(--success-light);
      color: var(--success);
    }

    .recent-upload-icon svg {
      width: 20px;
      height: 20px;
    }

    .recent-upload-info {
      flex: 1;
      min-width: 0;
    }

    .recent-upload-name {
      font-weight: 500;
      color: var(--text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .recent-upload-vendor {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .recent-upload-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: var(--space-1);
      flex-shrink: 0;
    }

    .recent-upload-amount {
      font-weight: 600;
      color: var(--text-primary);
    }

    .recent-upload-time {
      font-size: var(--text-xs);
      color: var(--text-secondary);
    }

    .recent-upload-status {
      flex-shrink: 0;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      padding: var(--space-1) var(--space-2);
      border-radius: var(--radius-full);
      font-size: var(--text-xs);
      font-weight: 500;
    }

    .status-badge.status--success {
      background: var(--success-light);
      color: var(--success);
    }

    .status-badge.status--warning {
      background: var(--warning-light);
      color: #b45309;
    }

    .status-badge.status--error {
      background: var(--error-light);
      color: var(--error);
    }

    .status-badge.status--neutral {
      background: var(--gray-100);
      color: var(--text-secondary);
    }

    .recent-upload-action {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      border-radius: var(--radius);
      color: var(--text-secondary);
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    }

    .recent-upload-action:hover {
      background: var(--gray-200);
      color: var(--text-primary);
    }

    .recent-upload-action svg {
      width: 16px;
      height: 16px;
    }

    .empty-message {
      text-align: center;
      padding: var(--space-6);
      color: var(--text-secondary);
      font-size: var(--text-sm);
    }

    @media (max-width: 768px) {
      .recent-upload-item {
        flex-wrap: wrap;
        gap: var(--space-3);
      }

      .recent-upload-info {
        flex: 1 1 calc(100% - 60px);
        order: 1;
      }

      .recent-upload-icon {
        order: 0;
      }

      .recent-upload-meta {
        order: 2;
        flex: 1;
        align-items: flex-start;
      }

      .recent-upload-status {
        order: 3;
      }

      .recent-upload-action {
        order: 4;
      }
    }
  `;

  document.head.appendChild(styles);
}

/**
 * Initialize page
 */
export function init() {
  // Look for existing upload-page container first (set by main.js)
  let pageContainer = document.getElementById('upload-page');
  
  // If not found, create it in the app container (standalone mode)
  if (!pageContainer) {
    const appContainer = document.getElementById('app');
    if (!appContainer) return;
    
    pageContainer = document.createElement('div');
    pageContainer.id = 'upload-page';
    pageContainer.className = 'page upload-page-container';
    appContainer.innerHTML = '';
    appContainer.appendChild(pageContainer);
  }

  // Initial render
  render();
}

/**
 * Cleanup
 */
export function destroy() {
  // Reset state on page leave
  state.view = 'upload';
  state.uploadedDocuments = [];
  state.selectedDocumentId = null;
  state.currentPreviewIndex = 0;
}

export default {
  init,
  destroy,
  getState: () => state,
};
